import { Box, Divider, Menu, MenuItem, useTheme } from '@mui/material';
import Popper from '@mui/material/Popper';
import { memo, useEffect, useMemo, useState, type FC } from 'react';
import { UMBetaBadge } from '@urbanmetrix/ui-kit';
import { BuildingSource, type BlockMetadata } from '@visualizer/types';
import { getGrid } from 'store/LoadingStore';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import { DEVELOPMENT_BLOCK_TYPES } from './DevelopmentBlockTypes';
import { PIPELINE_MENU_LAYOUT, type PipelineMenuBlockConfig } from './PipelineMenuLayout';

type PipelineAddMenuProps = {
    anchorEl: HTMLElement | null;
    open: boolean;
    isScalarMode: boolean;
    availableBlocks: BlockMetadata[];
    onSelect: (block: BlockMetadata) => void;
    onClose: () => void;
};

const DESCRIPTION_DISTANCE_PX = 4;
const DESCRIPTION_VIEWPORT_GUTTER_PX = 4;

const PipelineAddMenu: FC<PipelineAddMenuProps> = ({ anchorEl, open, isScalarMode, availableBlocks, onSelect, onClose }) => {
    const [hoveredItem, setHoveredItem] = useState<{ anchor: HTMLElement; description: string } | null>(null);
    const theme = useTheme();

    useEffect(() => {
        setHoveredItem(null);
    }, [open]);

    const handleClose = () => {
        setHoveredItem(null);
        onClose();
    };

    const handleSelect = (block: BlockMetadata) => {
        onSelect(block);
        handleClose();
    };

    const disableFilter = (block: BlockMetadata) => {
        if (
            isScalarMode &&
            (block.type === BlockKeys.Projection ||
                block.type === BlockKeys.Unobstructed ||
                block.type === BlockKeys.PvPotentials ||
                block.type === BlockKeys.ShgPotentials)
        ) {
            return true;
        }
        switch (block.type) {
            case BlockKeys.CloseToProjSketch: {
                const buildings = getGrid().getActiveGeodata().dataObject?.Buildings;
                const hasProjectSketch = buildings?.hasVisibleBuildings(BuildingSource.SKETCH) ?? false;
                return !hasProjectSketch;
            }
            default:
                return false;
        }
    };

    const orderedEntries = useMemo(() => {
        const byType = new Map(availableBlocks.map((block) => [block.type, block]));
        const sequence: Array<{ kind: 'divider' } | { kind: 'block'; block: BlockMetadata; config: PipelineMenuBlockConfig }> = [];

        for (const item of PIPELINE_MENU_LAYOUT) {
            if (item === 'divider') {
                sequence.push({ kind: 'divider' });
                continue;
            }

            const block = byType.get(item.type);
            if (block) {
                sequence.push({ kind: 'block', block, config: item });
            }
        }

        return sequence.filter((entry, index, arr) => {
            if (entry.kind !== 'divider') return true;
            const hasBlockBefore = arr.slice(0, index).some((x) => x.kind === 'block');
            const hasBlockAfter = arr.slice(index + 1).some((x) => x.kind === 'block');
            const prevIsDivider = index > 0 && arr[index - 1]?.kind === 'divider';
            return hasBlockBefore && hasBlockAfter && !prevIsDivider;
        });
    }, [availableBlocks]);

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const descriptionWidth =
        hoveredItem && viewportWidth > 0
            ? Math.max(
                  96,
                  Math.floor(
                      viewportWidth -
                          hoveredItem.anchor.getBoundingClientRect().right -
                          DESCRIPTION_DISTANCE_PX -
                          DESCRIPTION_VIEWPORT_GUTTER_PX
                  )
              )
            : undefined;

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        ml: '-4px',
                        '& .MuiMenuItem-root': {
                            px: '5px',
                        },
                    },
                }}
            >
                {orderedEntries.map((entry, index) => {
                    if (entry.kind === 'divider') {
                        return <Divider key={`divider-${index}`} />;
                    }

                    const { block, config } = entry;
                    const Icon = config.Icon;
                    return (
                        <MenuItem
                            key={block.type}
                            data-block-type={block.type}
                            disabled={disableFilter(block)}
                            onClick={() => handleSelect(block)}
                            onMouseEnter={(event) =>
                                setHoveredItem({
                                    anchor: event.currentTarget,
                                    description: block.description,
                                })
                            }
                            onMouseLeave={() => setHoveredItem(null)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4 }}>
                                <Box sx={{ display: 'inline-flex' }}>
                                    <Icon className="icon-size-medium" />
                                </Box>
                                <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}
                                >
                                    <span>{block.name}</span>
                                    {DEVELOPMENT_BLOCK_TYPES.has(block.type) && (
                                        <UMBetaBadge theme={theme.palette.mode === 'dark' ? 'dark' : 'light'} marginTop={-0.5} />
                                    )}
                                </Box>
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
            {open && hoveredItem && (
                <Popper
                    open
                    anchorEl={hoveredItem.anchor}
                    placement="right"
                    className="pipeline-menu-description-popper"
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, DESCRIPTION_DISTANCE_PX],
                            },
                        },
                        {
                            name: 'flip',
                            enabled: false,
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                mainAxis: true,
                                altAxis: false,
                                tether: true,
                                padding: 8,
                            },
                        },
                    ]}
                >
                    <Box
                        className="pipeline-menu-description"
                        sx={
                            descriptionWidth
                                ? {
                                      width: `${descriptionWidth}px`,
                                      maxWidth: 'none',
                                  }
                                : undefined
                        }
                    >
                        {hoveredItem.description}
                    </Box>
                </Popper>
            )}
        </>
    );
};

export default memo(PipelineAddMenu);
